import type { ITranslationOption } from '~/types/option'
import type { IPlaceholderMap, IPageContainer } from '~/types/page-container'
import type {
  IPageContainerFormValues,
  IPlaceholderConfig,
  IPlaceholderFormValue,
  IPlaceholderRule,
} from '~/types/template'

import { applicationSummaryMock } from './application-summary'
import { assetMock } from './asset'
import { iapMock } from './iap'

import { DEVICE_TYPE } from '~/const'
import { DEFAULT_IAP_PRICE_COUNTRY } from '~/const/template'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { PLACEHOLDER_RULE_TYPES, TEMPLATE_TYPE } from '~/types/template'

export const pageContainerMock: IPageContainer = {
  app_key: 'publishing_sample',
  capabilities: [
    'orientation_portrait',
    'action_continue',
    'action_buy',
    'action_restore',
    'action_return_app',
    'action_open_url',
    'action_setContextualStates',
  ],
  created_at: '2023-01-16T16:42:19.308209Z',
  created_by: 'c67fc8b4-f2d8-4692-bd80-2078fba80ba8',
  device_type: 'ios',
  has_stopped_asset: false,
  is_unity: false,
  navigation_target_outputs: [],
  orientation: 'portrait',
  placeholder_actions_values: [
    {
      placeholder: 'action_open_tos_4',
      value: 'www.mwm.io',
    },
  ],
  placeholder_asset_values: [],
  placeholder_color_values: [
    {
      placeholder: 'title_txt_1',
      value: '#cb3232',
    },
  ],
  placeholder_embedded_ui_values: [],
  placeholder_iap_values: [
    {
      id: 'iapForeverAllLanguages',
      placeholder: 'iap_1',
    },
  ],
  placeholder_rules: null,
  placeholder_text_values: [
    {
      placeholder: 'title_txt_1',
      text: 'Then [%s] / week',
      translation_key: 'store_button_subtitle_price_per_week',
    },
  ],
  platform_dependant_properties: {
    contextual_states: [
      {
        identifier: 'slide_variant',
        values: ['OPTION 01', 'OPTION 02'],
      },
    ],
    status_bar_style: 'light',
  },
  requirements: ['iOS_internal_sdk_format_1'],
  screenshot_file_ids: [],
  screenshot_file_urls: [],
  status: PAGE_CONTAINER_STATUS.DRAFT,
  swipeable: true,
  template: null,
  template_key: 'sampleTestOnboarding_0003',
  template_version: '1.0.0',
  title: 'Enter a title',
  type: TEMPLATE_TYPE.HORIZONTAL_MULTI_PAGE_CONTAINER,
  updated_at: '2023-01-16T16:42:19.30821Z',
  updated_by: 'c67fc8b4-f2d8-4692-bd80-2078fba80ba8',
  uuid: '3d5e9e30-13f4-481c-a302-e7bfdd6670d5',
}

export const emptyPageContainer = {
  ...pageContainerMock,
  placeholder_actions_values: [],
  placeholder_asset_values: [],
  placeholder_color_values: [],
  placeholder_iap_values: [],
  placeholder_text_values: [],
}

export const pageContainerAssetMock = {
  id: assetMock.id,
  placeholder: 'asset_1',
}

export const pageContainerWithAssets = {
  ...emptyPageContainer,
  placeholder_asset_values: [pageContainerAssetMock],
}

export const pageContainerIapMock = {
  id: iapMock[0].sku,
  placeholder: 'iap_1',
}

export const pageContainerWithIaps = {
  ...emptyPageContainer,
  placeholder_iap_values: [pageContainerIapMock],
}

export const pageContainerTranslationMock = {
  placeholder: 'text_1',
  text: 'Continue',
  translation: {
    device_type: DEVICE_TYPE.IOS,
    key: 'common.continue',
    length: 8,
    lengths: {
      ar: 9,
    },
    locale: 'en',
    lokalise_project_id: '582644755b56e6abed34f6.20024562',
    lokalise_project_name: 'Common Apps',
    objectID: '651725048#ios',
    value: 'Continue',
  },
  translation_key: 'common.continue',
}

export const pageContainerWithTexts = {
  ...emptyPageContainer,
  placeholder_text_values: [pageContainerTranslationMock],
}

export const pageContainerColorMock = {
  placeholder: 'color_1',
  value: '#FFFFF',
}

export const pageContainerWithColors = {
  ...emptyPageContainer,
  placeholder_color_values: [pageContainerColorMock],
}

export const pageContainerActionMock = {
  placeholder: 'action_1',
  value: 'mwm.io',
}

export const pageContainerWithActions = {
  ...emptyPageContainer,
  placeholder_actions_values: [pageContainerActionMock],
}

export const iap = iapMock[0]

export const placeholdersOutput: IPlaceholderMap = {
  [pageContainerAssetMock.placeholder]: {
    asset: {
      fileType: assetMock.file_type,
      files: assetMock.files,
      id: assetMock.id,
      label: assetMock.name,
      status: assetMock.status,
      updatedAt: assetMock.updated_at,
      updatedBy: assetMock.updated_by,
      value: assetMock.id,
    },
  },
  [pageContainerIapMock.placeholder]: {
    iap: {
      label: iap.title,
      price: iap.prices[DEFAULT_IAP_PRICE_COUNTRY],
      status: iap.status || '',
      value: iap.sku,
    },
  },
  [pageContainerTranslationMock.placeholder]: {
    translation: {
      label: pageContainerTranslationMock.translation_key,
      params: [],
      text: pageContainerTranslationMock.text,
      translation: pageContainerTranslationMock.translation,
      value: pageContainerTranslationMock.translation_key,
    },
  },
  [pageContainerColorMock.placeholder]: {
    color: pageContainerColorMock.value,
  },
  [pageContainerActionMock.placeholder]: {
    action: pageContainerActionMock.value,
  },
}

export const placeholderConfigMock: IPlaceholderConfig = {
  category: 'category_1',
  className: '',
  component: () => <></>,
  defaultValue: {
    action: 'goToUrl.com',
  },
  deviceType: 'ios',
  hiddenFields: ['color'],
  iapKey: 'iap_1',
  placeholderKey: pageContainerTranslationMock.placeholder,
  type: ['color', 'translation', 'action'],
}

export const placeholderFormValuesMock: Omit<IPlaceholderFormValue, 'fieldName'>[] = [
  {
    category: placeholderConfigMock.category,
    className: placeholderConfigMock.className,
    hiddenFields: placeholderConfigMock.hiddenFields,
    iapKey: placeholderConfigMock.iapKey,
    key: pageContainerTranslationMock.placeholder,
    type: 'color',
    value: '',
  },
  {
    category: placeholderConfigMock.category,
    className: placeholderConfigMock.className,
    hiddenFields: placeholderConfigMock.hiddenFields,
    iapKey: placeholderConfigMock.iapKey,
    key: pageContainerTranslationMock.placeholder,
    type: 'translation',
    value: placeholdersOutput[pageContainerTranslationMock.placeholder].translation as ITranslationOption,
  },
  {
    category: placeholderConfigMock.category,
    className: placeholderConfigMock.className,
    hiddenFields: placeholderConfigMock.hiddenFields,
    iapKey: placeholderConfigMock.iapKey,
    key: pageContainerTranslationMock.placeholder,
    type: 'action',
    value: 'goToUrl.com',
  },
]

export const formValuesWithFieldName = placeholderFormValuesMock.map((placeholder, index) => ({
  ...placeholder,
  fieldName: `placeholders.${index}.value`,
}))

export const templateVersionMock = '1.0.0'

export const placeholderRulesMock: IPlaceholderRule[] = [
  {
    created_at: '2023-01-30T14:40:41.881892Z',
    created_by: 'dynamic-screen-ios-tools',
    placeholder_key: 'continue_txt_3',
    placeholder_type: 'translation',
    rule_key: 'continue_txt_3-min',
    template_version_id: 'tios3_toggle',
    type: PLACEHOLDER_RULE_TYPES.MIN_LENGTH,
    updated_at: '2023-01-30T14:40:41.881892Z',
    updated_by: 'dynamic-screen-ios-tools',
    value: 0,
  },
  {
    created_at: '2023-01-30T14:40:41.881894Z',
    created_by: 'dynamic-screen-ios-tools',
    placeholder_key: 'continue_txt_3',
    placeholder_type: 'translation',
    rule_key: 'continue_txt_3-max',
    template_version_id: 'tios3_toggle',
    type: PLACEHOLDER_RULE_TYPES.MAX_LENGTH,
    updated_at: '2023-01-30T14:40:41.881894Z',
    updated_by: 'dynamic-screen-ios-tools',
    value: 50,
  },
]

export const pageContainerFormValuesMock: IPageContainerFormValues = {
  isUnity: false,
  placeholders: formValuesWithFieldName,
  screenshotFileIds: [],
  selectedApp: applicationSummaryMock,
  templateKey: pageContainerMock.template_key,
  templateVersion: pageContainerMock.template_version,
  title: pageContainerMock.title,
}
